/* ==========================================================================
   oh-kl.js — Optimal Human · KIDS greens listicle

   Adds ONLY the two primitives a listicle needs that neither oh-engine.js nor
   oh-tf.js provides. Everything else still comes from those two files and is
   never re-implemented here:
     oh-engine.js  [data-reveal] [data-parallax] [data-countup] [data-oh-drift]
     oh-tf.js      [data-tf-mag] [data-tf-collapse] [data-tf-stickybar]
                   [data-tf-scrub] [data-tf-pin] [data-tf-scrollhref]

   WHY THIS FILE EXISTS AT ALL
   oh-tf.js's [data-tf-scrub] is ELEMENT-relative — it writes 0 as the element
   enters from the bottom and 1 as it leaves at the top. A reading-progress rail
   needs DOCUMENT progress, which is a different number. Rather than abuse scrub
   (whose semantics other sections depend on) this file adds:

     [data-kl-progress]   document scroll progress → --p on the element
     [data-kl-toc]        scroll-spy that marks the active list item in the
                          contents row, via IntersectionObserver

   Editor-safe in the same way oh-tf.js is: OHKL.scan(root) rebinds everything,
   every bind is guarded by a data-kl-bound-* attribute so a rescan cannot double
   bind, and the file rescans on shopify:section:load.

   Both effects are progressive enhancement. Under prefers-reduced-motion the
   rail does not bind (oh-kl.css also display:none's it) and the scroll-spy
   degrades to plain anchor links that still work. Nothing here is ever the only
   route to content.
   ========================================================================== */
(function () {
  'use strict';

  if (window.OHKL && window.OHKL.__loaded) return;
  var OHKL = window.OHKL = window.OHKL || {};
  OHKL.__loaded = true;

  var reduced = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  function collect(scope, sel) {
    var list = [].slice.call(scope.querySelectorAll(sel));
    if (scope.nodeType === 1 && scope.matches && scope.matches(sel)) list.unshift(scope);
    return list;
  }
  function once(el, key) {
    var a = 'data-kl-bound-' + key;
    if (el.hasAttribute(a)) return false;
    el.setAttribute(a, '');
    return true;
  }
  var clamp = function (v, a, b) { return v < a ? a : v > b ? b : v; };

  /* ------------------------------------------------------------------ *
   * Shared rAF ticker. Same shape as oh-tf.js's — deliberately separate so
   * neither file can break the other's loop, and a throwing consumer is
   * caught and skipped rather than killing every other consumer.
   * ------------------------------------------------------------------ */
  var consumers = [], ticking = false, listening = false;

  function tick() {
    ticking = false;
    for (var i = consumers.length - 1; i >= 0; i--) {
      if (!consumers[i].el.isConnected) { consumers.splice(i, 1); continue; }
      try { consumers[i].run(); } catch (_) { /* one bad consumer must not kill the loop */ }
    }
  }
  function request() { if (!ticking) { ticking = true; requestAnimationFrame(tick); } }
  function addConsumer(el, run) {
    consumers.push({ el: el, run: run });
    if (!listening) {
      listening = true;
      window.addEventListener('scroll', request, { passive: true });
      window.addEventListener('resize', request, { passive: true });
    }
    request();
  }

  /* ------------------------------------------------------------------ *
   * 1) READING PROGRESS — document scroll position 0→1, written to --p.
   *
   *    Uses documentElement.scrollHeight rather than body's: body{zoom:.9} is
   *    live on this theme and body metrics are the ones that scale. The
   *    documentElement values stay in layout px, which is what scrollTop is
   *    also measured in, so the ratio is consistent.
   *
   *    The denominator is guarded: on a page shorter than the viewport it is 0
   *    or negative, and dividing by it would write NaN into a CSS var, which
   *    silently kills the transform. In that case progress is simply 1.
   * ------------------------------------------------------------------ */
  function bindProgress(scope) {
    if (reduced) return;
    collect(scope, '[data-kl-progress]').forEach(function (el) {
      if (!once(el, 'progress')) return;
      addConsumer(el, function () {
        var d = document.documentElement;
        var span = d.scrollHeight - d.clientHeight;
        var p = span > 0 ? clamp((window.pageYOffset || d.scrollTop) / span, 0, 1) : 1;
        el.style.setProperty('--p', p.toFixed(4));
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * 2) CONTENTS SCROLL-SPY — marks which list item is currently on screen.
   *
   *    The chips are plain <a href="#ohkl-item-N"> links, so with JS off or
   *    broken they still navigate. This only adds the .is-here highlight.
   *
   *    rootMargin pulls the detection band to the upper-middle of the viewport
   *    (-45% bottom) so an item counts as "current" once its heading is in the
   *    reading zone, not the moment one pixel of it appears. Without that, two
   *    adjacent items are both intersecting for most of the scroll and the
   *    highlight flickers between them.
   * ------------------------------------------------------------------ */
  function bindToc(scope) {
    collect(scope, '[data-kl-toc]').forEach(function (nav) {
      if (!once(nav, 'toc')) return;
      if (!('IntersectionObserver' in window)) return;

      var chips = [].slice.call(nav.querySelectorAll('a[href^="#"]'));
      if (!chips.length) return;

      var map = {};
      var targets = [];
      chips.forEach(function (a) {
        var id = a.getAttribute('href').slice(1);
        var t = document.getElementById(id);
        if (!t) return;
        map[id] = a;
        targets.push(t);
      });
      if (!targets.length) return;

      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          var a = map[e.target.id];
          if (!a) return;
          if (e.isIntersecting) {
            chips.forEach(function (c) { c.classList.remove('is-here'); c.removeAttribute('aria-current'); });
            a.classList.add('is-here');
            a.setAttribute('aria-current', 'true');
          }
        });
      }, { rootMargin: '-18% 0px -45% 0px', threshold: 0 });

      targets.forEach(function (t) { io.observe(t); });
    });
  }

  /* ------------------------------------------------------------------ */
  OHKL.scan = function (root) {
    var scope = root || document;
    bindProgress(scope);
    bindToc(scope);
  };

  function init() { OHKL.scan(document); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  /* Theme editor: a section that is re-rendered arrives with no bound state. */
  document.addEventListener('shopify:section:load', function (e) {
    OHKL.scan(e.target || document);
  });
})();
